import React from "react";
import { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { Group } from "@visx/group";
import { scaleLinear } from "@visx/scale";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { GridRows, GridColumns } from "@visx/grid";
import { ParentSize } from "@visx/responsive";
import { localPoint } from "@visx/event";
import { extent } from "d3-array";
import { formatPrice, formatKm, type DealRating } from "~/lib/utils";
import { useT } from "~/lib/i18n";
import {
  type CompareDeal,
  CompareHoverCard,
  useCompareOptional,
} from "~/components/scan/compare";

interface Deal extends CompareDeal {
  dealScore: number | null;
}

const DOT_COLOR: Record<DealRating, string> = {
  super: "var(--deal-super)",
  good: "var(--deal-good)",
  fair: "var(--deal-fair)",
  high: "var(--deal-high)",
  over: "var(--deal-over)",
  unknown: "var(--ink-disabled)",
};

export function ScatterView({ deals }: { deals: Deal[] }): React.JSX.Element {
  const { t } = useT();
  const compare = useCompareOptional();
  const points = useMemo(
    () =>
      deals
        .filter((d) => d.currentPriceEur != null && d.currentKm != null && d.currentKm > 0)
        .map((d) => ({
          deal: d,
          id: d.id,
          km: d.currentKm!,
          price: d.currentPriceEur!,
          year: d.currentYear,
          rating: (d.dealRating ?? "unknown") as DealRating,
          score: d.dealScore ?? 50,
          title: d.currentTitle ?? "—",
          thumbnailUrl: d.thumbnailUrl ?? null,
        })),
    [deals],
  );

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-5">
      <div className="border border-[var(--divider-weak)] rounded-xl bg-[var(--surface-card)] p-4 sm:p-5">
        <div className="flex items-baseline justify-between mb-1 gap-3 flex-wrap">
          <span className="mono text-[10px] sm:text-[10.5px] uppercase tracking-[0.12em] text-ink-tertiary">
            {t("web.scatter.kicker")}
          </span>
          <Legend />
        </div>
        {compare && (
          <p className="text-[11px] text-ink-tertiary mb-3">{t("web.scatter.compareHint")}</p>
        )}
        <div className="h-[min(55vh,540px)] min-h-[300px]">
          <ParentSize debounceTime={50}>
            {({ width, height }) => (
              <ScatterChart width={width} height={height} points={points} />
            )}
          </ParentSize>
        </div>
        {points.length === 0 && (
          <p className="mt-3 text-center text-[12px] text-ink-tertiary">
            {t("web.scatter.empty")}
          </p>
        )}
      </div>
    </div>
  );
}

function Legend(): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-[11px] text-ink-tertiary">
      {(["super", "good", "fair", "high", "over"] as const).map((r) => (
        <span key={r} className="inline-flex items-center gap-1">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ background: DOT_COLOR[r] }}
          />
          <span className="capitalize hidden sm:inline">{r}</span>
        </span>
      ))}
    </div>
  );
}

interface PointData {
  deal: CompareDeal;
  id: string;
  km: number;
  price: number;
  year: number | null;
  rating: DealRating;
  score: number;
  title: string;
  thumbnailUrl: string | null;
}

interface ScatterChartProps {
  width: number;
  height: number;
  points: PointData[];
}

interface ViewDomain {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

function linearRegression(pts: Array<{ km: number; price: number }>): { slope: number; intercept: number } | null {
  if (pts.length < 3) return null;
  const n = pts.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (const p of pts) {
    sumX += p.km;
    sumY += p.price;
    sumXY += p.km * p.price;
    sumXX += p.km * p.km;
  }
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-10) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

function computeNaturalDomain(points: PointData[]): ViewDomain {
  const [, kmMax] = extent(points, (p) => p.km) as [number, number];
  const [pMin, pMax] = extent(points, (p) => p.price) as [number, number];
  return {
    xMin: 0,
    xMax: (kmMax ?? 100_000) * 1.05,
    yMin: Math.max(0, (pMin ?? 0) * 0.9),
    yMax: (pMax ?? 100_000) * 1.05,
  };
}

const ZOOM_STEP = 0.08;
const MIN_ZOOM = 0.2;

function zoomDomain(domain: ViewDomain, factor: number, cx: number, cy: number): ViewDomain {
  const xRange = domain.xMax - domain.xMin;
  const yRange = domain.yMax - domain.yMin;
  const newXRange = xRange * factor;
  const newYRange = yRange * factor;
  if (newXRange < 100 || newYRange < 100) return domain;

  const xMin = cx - (cx - domain.xMin) * factor;
  const yMin = cy - (cy - domain.yMin) * factor;
  return {
    xMin: Math.max(0, xMin),
    xMax: xMin + newXRange,
    yMin: Math.max(0, yMin),
    yMax: yMin + newYRange,
  };
}

function panDomain(domain: ViewDomain, dxPct: number, dyPct: number): ViewDomain {
  const xRange = domain.xMax - domain.xMin;
  const yRange = domain.yMax - domain.yMin;
  const dx = xRange * dxPct;
  const dy = yRange * dyPct;
  return {
    xMin: Math.max(0, domain.xMin + dx),
    xMax: domain.xMax + dx,
    yMin: Math.max(0, domain.yMin + dy),
    yMax: domain.yMax + dy,
  };
}

function ScatterChart({ width, height, points }: ScatterChartProps): React.JSX.Element | null {
  const { t, numberLocale } = useT();
  const compare = useCompareOptional();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltipState, setTooltipState] = useState<{ point: PointData; left: number; top: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; startDomain: ViewDomain } | null>(null);

  const naturalDomain = useMemo(() => computeNaturalDomain(points), [points]);
  const [domain, setDomain] = useState<ViewDomain>(naturalDomain);

  // Reset domain when data changes significantly
  useEffect(() => {
    setDomain(naturalDomain);
  }, [naturalDomain]);

  const isZoomed = domain !== naturalDomain &&
    (Math.abs(domain.xMin - naturalDomain.xMin) > 1 ||
     Math.abs(domain.xMax - naturalDomain.xMax) > 1 ||
     Math.abs(domain.yMin - naturalDomain.yMin) > 1 ||
     Math.abs(domain.yMax - naturalDomain.yMax) > 1);

  const canRender = width >= 100 && height >= 100;

  const layout = useMemo(() => {
    if (!canRender) return null;
    const compact = width < 480;
    const margin = {
      top: 16,
      right: compact ? 12 : 20,
      bottom: compact ? 44 : 52,
      left: compact ? 50 : 64,
    };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const xScale = scaleLinear<number>({
      domain: [domain.xMin, domain.xMax],
      range: [0, innerW],
    });
    const yScale = scaleLinear<number>({
      domain: [domain.yMin, domain.yMax],
      range: [innerH, 0],
    });
    const reg = linearRegression(points);
    return { compact, margin, innerW, innerH, xScale, yScale, reg };
  }, [canRender, width, height, points, domain]);

  // Native wheel listener with {passive: false} to actually prevent scroll
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!layout) return;
      const delta = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY), 100) / 100;
      const factor = 1 + delta * ZOOM_STEP * 3;
      const rect = svg.getBoundingClientRect();
      const x = e.clientX - rect.left - layout.margin.left;
      const y = e.clientY - rect.top - layout.margin.top;
      const dataX = layout.xScale.invert(x);
      const dataY = layout.yScale.invert(y);
      setDomain((d) => zoomDomain(d, factor, dataX, dataY));
    };
    svg.addEventListener("wheel", handler, { passive: false });
    return () => svg.removeEventListener("wheel", handler);
  }, [layout]);

  const handleZoomIn = useCallback(() => {
    setDomain((d) => {
      const cx = (d.xMin + d.xMax) / 2;
      const cy = (d.yMin + d.yMax) / 2;
      return zoomDomain(d, 1 - ZOOM_STEP, cx, cy);
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setDomain((d) => {
      const cx = (d.xMin + d.xMax) / 2;
      const cy = (d.yMin + d.yMax) / 2;
      return zoomDomain(d, 1 + ZOOM_STEP, cx, cy);
    });
  }, []);

  const handleReset = useCallback(() => {
    setDomain(naturalDomain);
  }, [naturalDomain]);

  const handlePointerDown = useCallback((e: React.PointerEvent<SVGElement>) => {
    if (e.button !== 0) return;
    const coords = localPoint(svgRef.current!, e);
    if (!coords) return;
    dragRef.current = { startX: coords.x, startY: coords.y, startDomain: domain };
    (e.target as SVGElement).setPointerCapture(e.pointerId);
  }, [domain]);

  const handlePointerMove = useCallback((e: React.PointerEvent<SVGElement>) => {
    if (!dragRef.current || !layout) return;
    const coords = localPoint(svgRef.current!, e);
    if (!coords) return;
    const dx = -(coords.x - dragRef.current.startX) / layout.innerW;
    const dy = (coords.y - dragRef.current.startY) / layout.innerH;
    setDomain(panDomain(dragRef.current.startDomain, dx, dy));
  }, [layout]);

  const handlePointerUp = useCallback((e: React.PointerEvent<SVGElement>) => {
    dragRef.current = null;
    (e.target as SVGElement).releasePointerCapture(e.pointerId);
  }, []);

  const handleDoubleClick = useCallback(() => {
    setDomain(naturalDomain);
  }, [naturalDomain]);

  // Touch pinch zoom state
  const pinchRef = useRef<{ d0: number; domain0: ViewDomain; cx: number; cy: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && layout) {
      e.preventDefault();
      const t1 = e.touches[0]!;
      const t2 = e.touches[1]!;
      const d0 = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const sx = midX - rect.left - layout.margin.left;
      const sy = midY - rect.top - layout.margin.top;
      const cx = layout.xScale.invert(sx);
      const cy = layout.yScale.invert(sy);
      pinchRef.current = { d0, domain0: domain, cx, cy };
    }
  }, [layout, domain]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const t1 = e.touches[0]!;
      const t2 = e.touches[1]!;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const factor = pinchRef.current.d0 / Math.max(dist, 1);
      const clamped = Math.max(MIN_ZOOM, Math.min(factor, 5));
      setDomain(zoomDomain(pinchRef.current.domain0, clamped, pinchRef.current.cx, pinchRef.current.cy));
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    pinchRef.current = null;
  }, []);

  if (!canRender || !layout) return null;

  const { compact, margin, innerW, innerH, xScale, yScale, reg } = layout;

  const handleMouseEnter = (p: PointData, event: React.MouseEvent) => {
    const coords = localPoint(event);
    setHoveredId(p.id);
    setTooltipState({
      point: p,
      left: coords?.x ?? 0,
      top: coords?.y ?? 0,
    });
  };

  const handleMouseLeave = () => {
    setHoveredId(null);
    setTooltipState(null);
  };

  const handleClick = (p: PointData) => {
    window.location.href = `/listing/${p.id}`;
  };

  return (
    <div className="relative">
      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
        <button
          type="button"
          onClick={handleZoomIn}
          className="h-8 w-8 flex items-center justify-center rounded-lg bg-[var(--surface-card)] border border-[var(--divider-strong)] text-ink-secondary hover:text-ink-primary hover:bg-[var(--surface-hover)] transition-colors shadow-sm"
          title="Zoom +"
          aria-label="Zoom in"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
        <button
          type="button"
          onClick={handleZoomOut}
          className="h-8 w-8 flex items-center justify-center rounded-lg bg-[var(--surface-card)] border border-[var(--divider-strong)] text-ink-secondary hover:text-ink-primary hover:bg-[var(--surface-hover)] transition-colors shadow-sm"
          title="Zoom −"
          aria-label="Zoom out"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
        {isZoomed && (
          <button
            type="button"
            onClick={handleReset}
            className="h-8 w-8 flex items-center justify-center rounded-lg bg-[var(--surface-card)] border border-[var(--divider-strong)] text-ink-secondary hover:text-accent hover:bg-[var(--surface-hover)] transition-colors shadow-sm"
            title="Réinitialiser le zoom"
            aria-label="Reset zoom"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
        )}
      </div>

      {/* Hint on mobile */}
      {!compact && isZoomed && (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <span className="text-[10px] text-ink-tertiary bg-[var(--surface-card)]/80 px-2 py-0.5 rounded-full border border-[var(--divider-weak)]">
            Double-clic pour réinitialiser
          </span>
        </div>
      )}

      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="select-none touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Group left={margin.left} top={margin.top}>
          {/* Clip path for zoomed view */}
          <defs>
            <clipPath id="scatter-clip">
              <rect width={innerW} height={innerH} />
            </clipPath>
          </defs>

          <GridRows
            scale={yScale}
            width={innerW}
            stroke="var(--divider-weak)"
            strokeDasharray="2 3"
            numTicks={compact ? 4 : 5}
            strokeOpacity={0.6}
          />
          <GridColumns
            scale={xScale}
            height={innerH}
            stroke="var(--divider-weak)"
            strokeDasharray="2 3"
            numTicks={compact ? 4 : 5}
            strokeOpacity={0.6}
          />

          {/* Drag / pan surface (below dots so they get hover/click) */}
          <rect
            width={innerW}
            height={innerH}
            fill="transparent"
            className="cursor-grab active:cursor-grabbing"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onDoubleClick={handleDoubleClick}
            style={{ touchAction: "none" }}
          />

          <g clipPath="url(#scatter-clip)">
            {reg && (
              <line
                x1={xScale(0)}
                y1={yScale(reg.intercept)}
                x2={xScale(domain.xMax)}
                y2={yScale(reg.slope * domain.xMax + reg.intercept)}
                stroke="var(--accent)"
                strokeWidth={1.5}
                strokeDasharray="6 4"
                opacity={0.5}
              />
            )}
            {points.map((p) => {
              const cx = xScale(p.km);
              const cy = yScale(p.price);
              if (cx < -10 || cx > innerW + 10 || cy < -10 || cy > innerH + 10) return null;
              const isHovered = hoveredId === p.id;
              const inCompare = compare?.isInCompare(p.id) ?? false;
              return (
                <circle
                  key={p.id}
                  cx={cx}
                  cy={cy}
                  r={isHovered || inCompare ? 7 : 3.5 + (p.score / 100) * 3.5}
                  fill={DOT_COLOR[p.rating]}
                  fillOpacity={isHovered || inCompare ? 1 : 0.75}
                  stroke={inCompare ? "var(--accent)" : isHovered ? "var(--ink-primary)" : "var(--surface-card)"}
                  strokeWidth={inCompare ? 2.5 : isHovered ? 2 : 1}
                  style={{ transition: "r 150ms, stroke-width 150ms" }}
                  onMouseEnter={(e) => handleMouseEnter(p, e)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleClick(p)}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                  className="cursor-pointer"
                />
              );
            })}
          </g>

          <AxisBottom
            top={innerH}
            scale={xScale}
            stroke="var(--divider-strong)"
            tickStroke="var(--divider-strong)"
            numTicks={compact ? 4 : 6}
            tickFormat={(v) => `${Math.round(Number(v) / 1000)}k`}
            label="Kilométrage"
            labelProps={{
              fill: "var(--ink-tertiary)",
              fontSize: compact ? 9 : 10,
              fontFamily: "var(--font-mono)",
              textAnchor: "middle" as const,
              dy: compact ? "2.6em" : "2.8em",
            }}
            tickLabelProps={() => ({
              fill: "var(--ink-tertiary)",
              fontSize: compact ? 9 : 10,
              fontFamily: "var(--font-mono)",
              textAnchor: "middle" as const,
              dy: "0.6em",
            })}
          />
          <AxisLeft
            scale={yScale}
            stroke="var(--divider-strong)"
            tickStroke="var(--divider-strong)"
            numTicks={compact ? 4 : 5}
            label="Prix"
            labelProps={{
              fill: "var(--ink-tertiary)",
              fontSize: compact ? 9 : 10,
              fontFamily: "var(--font-mono)",
              textAnchor: "middle" as const,
              angle: -90,
              dx: compact ? "-2em" : "-2.8em",
            }}
            tickFormat={(v) =>
              new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
                notation: "compact",
                maximumFractionDigits: 0,
              }).format(Number(v))
            }
            tickLabelProps={() => ({
              fill: "var(--ink-tertiary)",
              fontSize: compact ? 9 : 10,
              fontFamily: "var(--font-mono)",
              textAnchor: "end" as const,
              dx: "-0.4em",
              dy: "0.3em",
            })}
          />
        </Group>
      </svg>
      {tooltipState && compare && (
        <CompareHoverCard
          deal={tooltipState.point.deal}
          left={tooltipState.left}
          top={tooltipState.top}
          hint={t("web.compare.openListing")}
        />
      )}
      {tooltipState && !compare && (
        <div
          className="absolute pointer-events-none z-30"
          style={{ left: tooltipState.left, top: tooltipState.top - 64 }}
        >
          <div className="bg-[var(--surface-card)] border border-[var(--divider-strong)] rounded-lg shadow-lg px-3 py-2 max-w-[200px]">
            <p className="text-[11px] font-medium text-ink-primary line-clamp-2 leading-tight">
              {tooltipState.point.title}
            </p>
            <div className="mt-1 flex items-center gap-2 text-[10px] mono text-ink-secondary">
              <span>{formatPrice(tooltipState.point.price, { locale: numberLocale })}</span>
              <span className="opacity-40">·</span>
              <span>{formatKm(tooltipState.point.km, numberLocale)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
